import useUserStore from '@/store/userStore'
import Cookies from 'js-cookie'
import { useCallback, useEffect } from 'react'
import { useShallow } from 'zustand/shallow'

/**
 * Custom hook that persists user data and loading state.
 * It fetches user data if a valid token exists and data is not already available.
 * It also dispatches an action to end loading state.
 *
 * @returns An object containing the user data and loading state.
 */
const usePersistUser = () => {
  const { isAuthenticated, isLoading, endLoading, logout, fetchUserData } = useUserStore(
    useShallow((state) => ({
      isAuthenticated: state.isAuthenticated,
      isLoading: state.isLoading,
      endLoading: state.endLoading,
      fetchUserData: state.fetchUserData,
      logout: state.logout
    }))
  )
  const token = Cookies.get('jwt')

  const handlePersistUser = useCallback(async () => {
    try {
      await fetchUserData()
    } catch (error) {
      logout()
    } finally {
      endLoading()
    }
  }, [fetchUserData, logout, endLoading])

  useEffect(() => {
    if (token) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      handlePersistUser()
    } else {
      if (isAuthenticated) {
        logout()
      }
      endLoading()
    }
  }, [token, isAuthenticated, handlePersistUser, logout, endLoading])

  return { isAuthenticated, isLoading }
}

export default usePersistUser
